/**
 * Redux thunk action creators for a project's Autodesk (APS) connection.
 * Backed by the real APS API. See `facades/apsApi.ts`.
 *
 * Every thunk carries `projectId` through to its fulfilled action, because the
 * APS slice is keyed by project and the reducer has no other way to know which
 * entry an action belongs to.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as ApsApi from '../facades/apsApi';
import { ApiErrorPayload, toApiErrorPayload } from '../facades/apiFacade';
import {
    ApsConnection,
    ApsHub,
    ApsProject,
    ApsFolder,
    ApsFolderEntry,
    FolderContentType,
    RvtModelGraph,
    ModelGroup,
    ModelGroupMember,
} from '../../@types/projects';
import type { ThunkAPI } from '../stores/store';

type ApsThunkAPI = ThunkAPI & { rejectValue: ApiErrorPayload & { projectId: string } };

/** Attach the project so the reducer can route a rejection to the right entry. */
function reject(error: unknown, projectId: string) {
    return { ...toApiErrorPayload(error), projectId };
}

export interface ApsConnectionResult {
    projectId: string;
    connection: ApsConnection;
}

/**
 * A 404 here is not a failure: it is the answer "this project has never been
 * connected". The reducer reads the rejection's status and renders the connect
 * prompt rather than an error.
 */
export const fetchApsConnection = createAsyncThunk<
    ApsConnectionResult,
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/fetchConnection', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, connection: await ApsApi.getConnection(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

/** Returns the Autodesk consent URL. Opening it is the caller's job. */
export const startApsConnect = createAsyncThunk<
    { projectId: string; authorizeUrl: string },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/startConnect', async ({ accessToken, projectId }, thunkApi) => {
    try {
        const { authorize_url: authorizeUrl } = await ApsApi.startConnect(accessToken, projectId);
        return { projectId, authorizeUrl };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const disconnectAps = createAsyncThunk<
    { projectId: string },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/disconnect', async ({ accessToken, projectId }, thunkApi) => {
    try {
        await ApsApi.disconnect(accessToken, projectId);
        return { projectId };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const fetchApsHubs = createAsyncThunk<
    { projectId: string; hubs: ApsHub[] },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/fetchHubs', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, hubs: await ApsApi.getHubs(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const fetchApsHubProjects = createAsyncThunk<
    { projectId: string; hubId: string; accProjects: ApsProject[] },
    { accessToken: string; projectId: string; hubId: string },
    ApsThunkAPI
>('aps/fetchHubProjects', async ({ accessToken, projectId, hubId }, thunkApi) => {
    try {
        const accProjects = await ApsApi.getHubProjects(accessToken, projectId, hubId);
        return { projectId, hubId, accProjects };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

/**
 * Without `allowReplace` this fails with a plain 409 when the project is already
 * pinned to a different ACC project. That is deliberate on the backend: a
 * first-time link attempt must never silently move a live link. The panel
 * catches it and asks the admin to confirm a replacement.
 */
export const linkAccProject = createAsyncThunk<
    ApsConnectionResult,
    { accessToken: string; projectId: string; hubId: string; accProjectId: string; allowReplace?: boolean },
    ApsThunkAPI
>('aps/link', async ({ accessToken, projectId, hubId, accProjectId, allowReplace }, thunkApi) => {
    try {
        const connection = await ApsApi.linkAccProject(
            accessToken,
            projectId,
            { hubId, accProjectId },
            { allowReplace }
        );
        return { projectId, connection };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const unlinkAccProject = createAsyncThunk<
    { projectId: string },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/unlink', async ({ accessToken, projectId }, thunkApi) => {
    try {
        await ApsApi.unlinkAccProject(accessToken, projectId);
        return { projectId };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

// ── Model groups & the RVT link graph ───────────────────────────────────────

export const fetchTopFolders = createAsyncThunk<
    { projectId: string; folders: ApsFolder[] },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/fetchTopFolders', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, folders: await ApsApi.getTopFolders(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

/**
 * One page of one folder's children. The reducer appends `entries` to that
 * folder's accumulated subfolder list rather than replacing it, since the
 * caller is expected to dispatch this once per page while walking a folder
 * (see `useFolderExpand`) — each page renders as it arrives instead of the
 * whole walk blocking on its last page.
 */
export const fetchFolderContents = createAsyncThunk<
    { projectId: string; folderUrn: string; page: number; entries: ApsFolderEntry[]; pageCount: number | null },
    { accessToken: string; projectId: string; folderUrn: string; page: number; types?: FolderContentType[] },
    ApsThunkAPI
>('aps/fetchFolderContents', async ({ accessToken, projectId, folderUrn, page, types }, thunkApi) => {
    try {
        const contents = await ApsApi.getFolderContents(accessToken, projectId, folderUrn, { page, types });
        return { projectId, folderUrn, page, entries: contents.entries, pageCount: contents.page_count };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

/**
 * The catalog and its link graph, in one call. Best effort on the backend: a
 * model whose facts could not be read still appears, with those fields null,
 * so a 2xx here does not mean every node is fully populated.
 */
export const fetchRvtModels = createAsyncThunk<
    { projectId: string; graph: RvtModelGraph },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/fetchRvtModels', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, graph: await ApsApi.getRvtModels(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const fetchModelGroups = createAsyncThunk<
    { projectId: string; groups: ModelGroup[] },
    { accessToken: string; projectId: string },
    ApsThunkAPI
>('aps/fetchModelGroups', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, groups: await ApsApi.getModelGroups(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const createModelGroup = createAsyncThunk<
    { projectId: string; group: ModelGroup },
    { accessToken: string; projectId: string; name: string; members: ModelGroupMember[] },
    ApsThunkAPI
>('aps/createModelGroup', async ({ accessToken, projectId, name, members }, thunkApi) => {
    try {
        const group = await ApsApi.createModelGroup(accessToken, projectId, { name, members });
        return { projectId, group };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const replaceModelGroup = createAsyncThunk<
    { projectId: string; group: ModelGroup },
    { accessToken: string; projectId: string; groupId: string; name: string; members: ModelGroupMember[] },
    ApsThunkAPI
>('aps/replaceModelGroup', async ({ accessToken, projectId, groupId, name, members }, thunkApi) => {
    try {
        const group = await ApsApi.replaceModelGroup(accessToken, projectId, groupId, { name, members });
        return { projectId, group };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const deleteModelGroup = createAsyncThunk<
    { projectId: string; groupId: string },
    { accessToken: string; projectId: string; groupId: string },
    ApsThunkAPI
>('aps/deleteModelGroup', async ({ accessToken, projectId, groupId }, thunkApi) => {
    try {
        await ApsApi.deleteModelGroup(accessToken, projectId, groupId);
        return { projectId, groupId };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});
